import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/seo/SEO';
import { toast } from 'sonner';
import axios from 'axios';
import content from '../content.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export default function Contact() {
  const { title, subtitle, form, info } = content.contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
    honeypot: '', // Spam protection
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleConsentChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      consent: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.honeypot) {
      return;
    }

    // Validation
    if (!formData.name || !formData.company || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.consent) {
      toast.error('Please agree to be contacted about your enquiry');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast.success('Message sent successfully!');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: '',
          consent: false,
          honeypot: '',
        });
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send message. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title={content.seo.contact.title}
        description={content.seo.contact.description}
        canonical="https://dakshtechinfo.uk/contact"
      />
      
      <div data-testid="contact-page">
        {/* Hero Section */}
        <section className="section-padding bg-muted/30" data-testid="contact-hero">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto text-center"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
              >
                {title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-base md:text-lg text-muted-foreground"
              >
                {subtitle}
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="section-padding" data-testid="contact-form-section">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
              {/* Form */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                className="lg:col-span-2"
              >
                {isSuccess ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                    data-testid="contact-success"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-8 w-8 text-green-500" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
                    <p className="text-muted-foreground mb-8">
                      We've received your message and will get back to you within 24 hours.
                    </p>
                    <Button 
                      onClick={() => setIsSuccess(false)}
                      variant="outline"
                      data-testid="send-another-message"
                    >
                      Send Another Message
                    </Button>
                  </motion.div>
                ) : (
                  <Card className="border-border/50">
                    <CardContent className="p-6 md:p-8">
                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Honeypot field - hidden from users */}
                        <input
                          type="text"
                          name="honeypot"
                          value={formData.honeypot}
                          onChange={handleChange}
                          style={{ display: 'none' }}
                          tabIndex={-1}
                          autoComplete="off"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <motion.div variants={itemVariants} className="space-y-2">
                            <Label htmlFor="name">{form.name_label} *</Label>
                            <Input
                              id="name"
                              name="name"
                              placeholder={form.name_placeholder}
                              value={formData.name}
                              onChange={handleChange}
                              required
                              data-testid="contact-name-input"
                            />
                          </motion.div>
                          
                          <motion.div variants={itemVariants} className="space-y-2">
                            <Label htmlFor="company">{form.company_label} *</Label>
                            <Input
                              id="company"
                              name="company"
                              placeholder={form.company_placeholder}
                              value={formData.company}
                              onChange={handleChange}
                              required
                              data-testid="contact-company-input"
                            />
                          </motion.div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <motion.div variants={itemVariants} className="space-y-2">
                            <Label htmlFor="email">{form.email_label} *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              placeholder={form.email_placeholder}
                              value={formData.email}
                              onChange={handleChange}
                              required
                              data-testid="contact-email-input"
                            />
                          </motion.div>
                          
                          <motion.div variants={itemVariants} className="space-y-2">
                            <Label htmlFor="phone">{form.phone_label}</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              placeholder={form.phone_placeholder}
                              value={formData.phone}
                              onChange={handleChange}
                              data-testid="contact-phone-input"
                            />
                          </motion.div>
                        </div>

                        <motion.div variants={itemVariants} className="space-y-2">
                          <Label htmlFor="message">{form.message_label} *</Label>
                          <Textarea
                            id="message"
                            name="message"
                            placeholder={form.message_placeholder}
                            value={formData.message}
                            onChange={handleChange}
                            rows={6}
                            required
                            data-testid="contact-message-input"
                          />
                        </motion.div>

                        <motion.div variants={itemVariants} className="flex items-start gap-3">
                          <Checkbox
                            id="consent"
                            checked={formData.consent}
                            onCheckedChange={handleConsentChange}
                            data-testid="contact-consent-checkbox"
                          />
                          <Label 
                            htmlFor="consent" 
                            className="text-sm text-muted-foreground cursor-pointer"
                          >
                            {form.consent_label} *
                          </Label>
                        </motion.div>

                        <motion.div variants={itemVariants}>
                          <Button 
                            type="submit" 
                            size="lg" 
                            className="w-full md:w-auto"
                            disabled={isSubmitting}
                            data-testid="contact-submit-button"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="mr-2 h-4 w-4" />
                                {form.submit_button}
                              </>
                            )}
                          </Button>
                        </motion.div>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </motion.div>

              {/* Contact Info */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                className="space-y-6"
              >
                <motion.div variants={itemVariants}>
                  <h3 className="text-xl font-semibold mb-6">{info.title}</h3>
                  
                  <Card className="border-border/50 mb-6">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Mail className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-medium mb-1">Email Us</p>
                          <a 
                            href={`mailto:${info.email}`}
                            className="text-accent hover:underline"
                            data-testid="contact-email-link"
                          >
                            {info.email}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <p className="font-medium mb-1">Response Time</p>
                          <p className="text-muted-foreground text-sm">
                            {info.response_time}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
